import React from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
    TextB, TextItalic, ListBullets, ListNumbers, ArrowCounterClockwise,
} from "@phosphor-icons/react";

/**
 * RichTextEditor — TipTap-powered editor for the Prayers admin form.
 *
 * Stores content as sanitised HTML (TipTap's own output is XSS-safe, but
 * the rendering surface still pipes through DOMPurify defence-in-depth).
 * The toolbar exposes the four product-spec essentials: Bold, Italic,
 * Text colour and Bullet/Ordered lists.
 *
 * Backward compatibility: when `value` arrives as plain text (legacy
 * prayers stored as raw `\n`-joined strings), TipTap renders it verbatim
 * inside a single paragraph so the admin can immediately keep editing.
 *
 * Props
 *   value     : current HTML string (or legacy plain text).
 *   onChange  : called with the freshest HTML on every keystroke.
 *   placeholder, testId, ariaLabel: small a11y/test affordances.
 */

// Colour palette the admin can pick from. We keep the swatches small and
// readable on the existing sand background; the user can clear the
// colour by tapping the "x" reset chip.
const COLOR_PALETTE = [
    { id: "default", label: "Default",  hex: null },
    { id: "red",     label: "Red",      hex: "#B0413E" },
    { id: "blue",    label: "Blue",     hex: "#1F4E79" },
    { id: "green",   label: "Green",    hex: "#256D2A" },
    { id: "amber",   label: "Amber",    hex: "#B8770B" },
    { id: "violet",  label: "Violet",   hex: "#6B3FA0" },
];

// Convert a legacy plain-text body into minimal HTML so TipTap can edit
// it without losing the user's paragraph breaks. Anything that already
// contains tags is left intact.
function normaliseInitial(value) {
    if (!value) return "";
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(value);
    if (looksLikeHtml) return value;
    return value
        .split(/\n{2,}/)
        .map((p) => p.replace(/\n/g, "<br/>"))
        .map((p) => `<p>${p}</p>`)
        .join("");
}

function ToolbarButton({ active, disabled, onClick, label, testId, children }) {
    return (
        <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            aria-pressed={!!active}
            title={label}
            data-testid={testId}
            className={[
                "inline-flex items-center justify-center w-9 h-9 rounded-md transition-colors",
                "border border-sand-300 ui-sans text-sm",
                active ? "bg-stone900 text-sand-50 border-stone900" : "bg-white text-stone900 hover:border-sangre",
                disabled ? "opacity-40 cursor-not-allowed" : "",
            ].join(" ")}
        >
            {children}
        </button>
    );
}

export default function RichTextEditor({
    value,
    onChange,
    placeholder,
    testId = "prayer-rich-editor",
    ariaLabel,
}) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: false,           // not in the product spec
                blockquote: false,
                codeBlock: false,
                horizontalRule: false,
                strike: false,
            }),
            TextStyle,
            Color,                        // <span style="color:#...">
        ],
        content: normaliseInitial(value),
        editorProps: {
            attributes: {
                class: "tiptap-area focus:outline-none min-h-[14rem] px-3 py-2.5 reading-serif text-sm",
                "data-testid": `${testId}-area`,
                "aria-label": ariaLabel || placeholder || "",
            },
        },
        onUpdate: ({ editor: ed }) => {
            // TipTap returns an empty paragraph `<p></p>` for an empty
            // document. Surface "" upward so required-field validation
            // works the same way as a textarea.
            const html = ed.getHTML();
            const isEmpty = html.replace(/<p>\s*<\/p>/g, "").trim() === "";
            onChange(isEmpty ? "" : html);
        },
    });

    // Keep editor content in sync when the parent resets / opens a
    // different prayer for editing. We compare to the current HTML so
    // typing doesn't trigger feedback loops.
    React.useEffect(() => {
        if (!editor) return;
        const current = editor.getHTML();
        const next = normaliseInitial(value || "");
        if (next && next !== current) {
            editor.commands.setContent(next, { emitUpdate: false });
        } else if (!value && current && current !== "<p></p>") {
            editor.commands.setContent("", { emitUpdate: false });
        }
    }, [value, editor]);

    if (!editor) {
        return (
            <div
                className="border border-sand-300 rounded-md min-h-[14rem] bg-white"
                data-testid={`${testId}-loading`}
                aria-busy="true"
            />
        );
    }

    return (
        <div
            className="border border-sand-300 rounded-md bg-white focus-within:border-sangre"
            data-testid={testId}
        >
            <div
                className="flex flex-wrap items-center gap-1 px-2 py-1.5 border-b border-sand-300 bg-sand-50/70"
                role="toolbar"
                aria-label={ariaLabel ? `${ariaLabel} — formato` : "Formato"}
            >
                <ToolbarButton
                    active={editor.isActive("bold")}
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    label="Negrita"
                    testId={`${testId}-bold`}
                >
                    <TextB size={16} weight="bold" />
                </ToolbarButton>
                <ToolbarButton
                    active={editor.isActive("italic")}
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    label="Cursiva"
                    testId={`${testId}-italic`}
                >
                    <TextItalic size={16} weight="bold" />
                </ToolbarButton>

                <span className="w-px h-6 bg-sand-300 mx-1" aria-hidden="true" />

                <ToolbarButton
                    active={editor.isActive("bulletList")}
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    label="Lista con viñetas"
                    testId={`${testId}-bullet-list`}
                >
                    <ListBullets size={16} weight="bold" />
                </ToolbarButton>
                <ToolbarButton
                    active={editor.isActive("orderedList")}
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    label="Lista numerada"
                    testId={`${testId}-ordered-list`}
                >
                    <ListNumbers size={16} weight="bold" />
                </ToolbarButton>

                <span className="w-px h-6 bg-sand-300 mx-1" aria-hidden="true" />

                {/* Colour swatches. Each pill applies (or clears) the
                    text colour on the current selection / next typed
                    text. The active swatch is announced via aria-pressed. */}
                <div
                    className="flex items-center gap-1"
                    role="group"
                    aria-label="Color de texto"
                    data-testid={`${testId}-color-row`}
                >
                    {COLOR_PALETTE.map((c) => {
                        const active = c.hex
                            ? editor.isActive("textStyle", { color: c.hex })
                            : !editor.getAttributes("textStyle").color;
                        return (
                            <button
                                key={c.id}
                                type="button"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    if (!c.hex) {
                                        editor.chain().focus().unsetColor().run();
                                    } else {
                                        editor.chain().focus().setColor(c.hex).run();
                                    }
                                }}
                                title={c.label}
                                aria-label={`Color ${c.label}`}
                                aria-pressed={active}
                                data-testid={`${testId}-color-${c.id}`}
                                className={[
                                    "relative w-7 h-7 rounded-full transition-transform",
                                    "ring-1 ring-stone900/15",
                                    active ? "ring-2 ring-stone900 scale-110" : "hover:scale-105",
                                    !c.hex ? "bg-white" : "",
                                ].join(" ")}
                                style={c.hex ? { backgroundColor: c.hex } : undefined}
                            >
                                {!c.hex && (
                                    <ArrowCounterClockwise
                                        size={12}
                                        weight="bold"
                                        className="absolute inset-0 m-auto text-stone900"
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            <EditorContent editor={editor} />
        </div>
    );
}
